// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ComplexSelector.tsx
 * @input Uses React, StyleX, Field, usePopover
 * @output Exports ComplexSelector component for custom selector surfaces
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update:
 * - /packages/core/src/ComplexSelector/ComplexSelector.doc.mjs
 * - /packages/core/src/ComplexSelector/ComplexSelector.test.tsx
 * - /packages/core/src/ComplexSelector/index.ts
 * - /apps/storybook/stories/ComplexSelector.stories.tsx
 */
import React, { useCallback, useId, useOptimistic, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Field, inputWrapperStyles } from "../Field/index.js";
import { Icon } from "../Icon/index.js";
import { Spinner } from "../Spinner/index.js";
import { useTranslator } from "../i18n/index.js";
import { layerAnimations } from "../Layer/layerAnimations.stylex.js";
import { usePopover } from "../Popover/usePopover.js";
import "../theme/tokens.stylex.js";
import { colorVars, durationVars, easeVars, radiusVars, sizeVars, spacingVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  triggerContainer: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    k8WAf4: "xce4md1",
    kg3NbH: "xrrkdod",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib xdk3u5w",
    kLWn49: "x1kq96og",
    kMwMTN: "x1tgivj0",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  trigger: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1txdalj",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kCS8Yb: "x1r8uery",
    k7Eaqz: "xeuugli",
    kmVPX3: "x1717udv",
    kogj98: "x1ghz6dp",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kWkggS: "xjbqb8w",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1qlqyl8",
    kLWn49: "x15bjb6t",
    kMwMTN: "x1heor9g",
    kkrTdU: "x1ypdohk",
    kI3sdo: "x1a2a7pz",
    kaIpWk: "xh6dtrn",
    $$css: true
  },
  triggerText: {
    kzQI83: "x1iyjqo2",
    k7Eaqz: "xeuugli",
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    k9WMMc: "x1yc453h",
    $$css: true
  },
  placeholder: {
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  triggerIcon: {
    kmuXW: "x2lah0s",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    k1ekBW: "x11xpdln",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    k3nNDw: "x1g0ag68",
    kMwMTN: "xv9yike",
    $$css: true
  },
  triggerIconOpen: {
    k3aq6I: "x19jd1h0",
    $$css: true
  },
  popover: {
    k7Eaqz: "xrzjruh",
    keoZOQ: "xcsaf9d",
    $$css: true
  },
  content: {
    kB7OPa: "x9f619",
    kskxy: "xpnbb6b",
    kVQacm: "xysyzu8",
    kmVPX3: "x1b2ylru",
    $$css: true
  },
  sm: {
    kAzted: "x1gdqauq",
    $$css: true
  },
  md: {
    kAzted: "x1uogy3y",
    $$css: true
  },
  lg: {
    kAzted: "x1xf4nls",
    $$css: true
  },
  disabled: {
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  focusRing: {
    k8PahR: "x1oqel4m",
    kfQpEX: "x1fanpfn",
    $$css: true
  }
};
/**
 * A selector shell for rich, custom selection surfaces.
 *
 * ComplexSelector owns the field, trigger, popover, focus restore, and async
 * change action flow. Consumers provide the dialog content as a render function,
 * using the supplied `value`, `onChange`, and `close` helpers to compose the
 * right accessible structure for the custom selector.
 *
 * @example
 * ```
 * <ComplexSelector
 *   label="Fruit"
 *   value={value}
 *   onChange={setValue}
 *   triggerLabel={`${value.fruit} ${value.ripeness}`}>
 *   {(value, onChange, close) => (
 *     <FruitGrid
 *       value={value}
 *       onChange={nextValue => {
 *         onChange(nextValue);
 *         close();
 *       }}
 *     />
 *   )}
 * </ComplexSelector>
 * ```
 */
export function ComplexSelector({
  label,
  value,
  onChange,
  changeAction,
  children,
  triggerLabel,
  placeholder: placeholderFromProps,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  isLoading = false,
  status,
  statusVariant = 'attached',
  labelTooltip,
  size = 'md',
  width,
  placement = 'below',
  contentXstyle,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ...props
}) {
  const t = useTranslator();
  const placeholder = placeholderFromProps ?? t('@astryx.selector.placeholder');
  const triggerId = useId();
  const labelId = useId();
  const contentId = useId();
  const descriptionId = useId();
  const statusMessageId = useId();
  const ariaDescribedBy = [description ? descriptionId : null, status?.message ? statusMessageId : null].filter(id => id != null).join(' ') || undefined;
  const [isPending, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || isPending;
  const popover = usePopover({
    dialogLabel: label,
    hasCloseButton: false,
    hasAutoFocus: true,
    onHide: () => {
      document.getElementById(triggerId)?.focus();
    }
  });
  const commitValue = useCallback(nextValue => {
    onChange?.(nextValue);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(nextValue);
        await changeAction(nextValue);
      });
    }
  }, [changeAction, onChange, setOptimisticValue, startTransition]);
  const triggerContent = triggerLabel ?? placeholder;
  const content = /*#__PURE__*/_jsx("div", {
    id: contentId,
    ...stylex.props(styles.content, contentXstyle),
    children: children(optimisticValue, commitValue, popover.hide, {
      isOpen: popover.isOpen,
      isBusy,
      triggerId,
      contentId
    })
  });
  const selectorContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ref: popover.triggerRef,
      "data-testid": testId,
      ...props,
      onClick: () => {
        if (!isDisabled) {
          popover.toggle();
        }
      },
      ...mergeProps(themeProps('complex-selector', {
        size,
        status: status?.type ?? null
      }), stylex.props(inputWrapperStyles.base, styles.triggerContainer, styles[size], styles.focusRing, isDisabled && inputWrapperStyles.disabled, isDisabled && styles.disabled, triggerLabel == null && styles.placeholder, xstyle), className, style),
      children: [/*#__PURE__*/_jsx("button", {
        id: triggerId,
        type: "button",
        "aria-haspopup": "dialog",
        "aria-expanded": popover.isOpen,
        "aria-controls": contentId,
        "aria-describedby": ariaDescribedBy,
        "aria-labelledby": labelId,
        "aria-required": isRequired ? 'true' : undefined,
        "aria-invalid": status?.type === 'error' ? 'true' : undefined,
        "aria-busy": isBusy || undefined,
        disabled: isDisabled,
        onKeyDown: event => {
          if (event.key === 'ArrowDown' && !popover.isOpen && !isDisabled) {
            event.preventDefault();
            popover.show();
          }
        },
        ...{
          className: "x78zum5 x6s0dn4 x1qughib x1txdalj x1iyjqo2 xs83m0k x1r8uery xeuugli x1717udv x1ghz6dp xc342km xng3xce xjbqb8w xjb2p0i x1qlqyl8 x15bjb6t x1heor9g x1ypdohk x1a2a7pz xh6dtrn"
        },
        children: /*#__PURE__*/_jsx("span", {
          ...{
            className: "x1iyjqo2 xeuugli xb3r6kr xlyipyv xuxw1ft x1yc453h"
          },
          children: triggerContent
        })
      }), isBusy && /*#__PURE__*/_jsx(Spinner, {
        size: "sm"
      }), /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike"
          },
          1: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike x19jd1h0"
          }
        }[!!popover.isOpen << 0],
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "chevronDown",
          size: "sm",
          color: "inherit",
          ...themeProps('complex-selector-indicator-icon', {
            state: popover.isOpen ? 'expanded' : 'collapsed'
          })
        })
      })]
    }), popover.render(content, {
      placement,
      alignment: 'start',
      xstyle: [styles.popover, layerAnimations[placement]]
    })]
  });
  return /*#__PURE__*/_jsx(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: triggerId,
    descriptionID: description ? descriptionId : undefined,
    labelID: labelId,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageId : undefined
    } : undefined,
    statusVariant: statusVariant,
    labelTooltip: labelTooltip,
    width: width,
    children: selectorContent
  });
}
ComplexSelector.displayName = 'ComplexSelector';